// dependencies
import React from "react";
import { SWRConfig } from "swr";

// containers
import RoomListContainer from "../../containers/RoomList";

// constants
import { CONTENT_TYPE_CATEGORY } from "@cloudspire/legacy-resources/src/constants/content";
import { searchDefaultParameters } from "../../constants/search";

// api
import { contentRoomListPage } from "@cloudspire/legacy-resources/src/api/content";

// libraries
import { generateUri } from "@cloudspire/legacy-shared/src/libraries";

import {
  contentGetSlugFromCategoryAlias,
  contentGetAliasFromSlugForCampaigns,
} from "@cloudspire/legacy-resources/src/helpers/content";
import { contentTypeRedirect } from "../../helpers/content";
import { nextRedirect } from "@cloudspire/legacy-resources/src/helpers/next";

// contexts
import { useRoute } from "@cloudspire/legacy-resources/src/contexts/route";
import { fetchFallback } from "@cloudspire/legacy-resources/src/libraries/utils/fetcher";

function RoomListPage(props) {
  const { useContent, category, fallback } = props;

  const route = useRoute();

  return (
    <SWRConfig value={{ fallback }}>
      <RoomListContainer
        key={route.getParameter("slug")}
        useContent={useContent}
        category={category}
      />
    </SWRConfig>
  );
}

RoomListPage.getInitialProps = async (ctx) => {
  const { isomorphicFetcher, route, configuration, routers } = ctx;

  const { frontRouter } = routers;

  const query = route.getUri().getParsedQuery();

  const hasPreview = "1" === query.preview;

  const { category: queryCategory } = query;

  if (queryCategory) {
    // Si une catégorie a été définie dans les query-params

    const slug = contentGetSlugFromCategoryAlias({
      contentList: configuration.channel.website.contents.list,
      categoryAlias: queryCategory,
    });

    const redirectUri = generateUri({
      router: frontRouter,
      name: "App.Action.RoomListWithSlug",
      query: {
        ...query,
        category: undefined,
      },
      parameters: {
        lang: route.getParameter("lang"),
        slug,
      },
    });

    return await nextRedirect({
      redirectionUri: redirectUri,
      code: 302,
      res: ctx.res,
    });
  }

  const slug = route.getParameter("slug");

  if ("App.Action.RoomListWithSlug" === route.getName()) {
    const content = configuration.channel.website.contents.list.find(
      (content) => {
        return slug === content.alias;
      }
    );

    if (
      // Si le slug de la page courante n’a pas la même valeur que l’alias du contenu.
      content &&
      content.slug !== slug
    ) {
      const redirectUri = generateUri({
        router: frontRouter,
        name: "App.Action.RoomListWithSlug",
        query,
        parameters: {
          lang: route.getParameter("lang"),
          slug: content.slug,
        },
      });

      return await nextRedirect({
        redirectionUri: redirectUri,
        code: 302,
        res: ctx.res,
      });
    }
  }

  const routeUseSlug = "App.Action.RoomListWithSlug" === route.getName();

  const isValidContentSlug = configuration.channel.website.contents.list.some(
    (content) => content.slug === slug
  );

  const isValidCategorySlug =
    configuration.room.categoryNameList.includes(slug);

  if (
    // Si l’url possède un slug mais que celui-ci n’est pas valide, on redirige vers la version sans slug.
    !hasPreview &&
    routeUseSlug &&
    !isValidContentSlug &&
    !isValidCategorySlug
  ) {
    const redirectUri = generateUri({
      router: frontRouter,
      name: "App.Action.RoomList",
      query,
      parameters: {
        lang: route.getParameter("lang"),
      },
    });

    return await nextRedirect({
      redirectionUri: redirectUri,
      code: 302,
      res: ctx.res,
    });
  }

  const useContent = hasPreview || (routeUseSlug && isValidContentSlug);

  let category = slug;

  let contentPageFallback;
  if (useContent) {
    let contentShow;

    [contentPageFallback, contentShow] = await fetchFallback({
      ...contentRoomListPage({
        preview: "1" === query.preview ? "1" : undefined,
        slug: route.getParameter("slug"),
        query: {
          ...searchDefaultParameters,
        },
      }),
      fetcher: isomorphicFetcher,
    });

    category = contentGetAliasFromSlugForCampaigns({
      contentList: configuration.channel.website.contents.list,
      slug,
    });

    await contentTypeRedirect({
      content: contentShow.data,
      ctx,
      frontRouter,
      lang: route.getParameter("lang"),
      pageType: [CONTENT_TYPE_CATEGORY],
    });
  }

  return {
    useContent,
    category,
    fallback: {
      ...contentPageFallback,
    },
  };
};

export default RoomListPage;
